import type { ApolloError } from '@apollo/client';
import { Box, Stack } from 'braid-design-system';
import type React from 'react';

import { JobsListLoader } from 'src/App/layouts/JobsListPage/JobsListLoader';
import { useQueryParam } from 'src/hooks/useQueryParam';

import { EmptyState } from '../EmptyState/EmptyState';
import { ErrorPage } from '../ErrorPage/ErrorPage';
import { JobSearchPanel } from '../JobSearchPanel/JobSearchPanel';
import { PaginationSection } from '../PaginationSection/PaginationSection';

interface MainContentProps {
  loading: boolean;
  error?: ApolloError;
  totalCount: number;
  isDraft?: boolean;
}

export const MainContent: React.FC<
  React.PropsWithChildren<MainContentProps>
> = ({ loading, error, totalCount, isDraft, children }) => {
  const { q: searchText, tab } = useQueryParam();

  if (loading) {
    return <JobsListLoader tab={tab} />;
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" padding="large">
        <ErrorPage />
      </Box>
    );
  }

  if (totalCount === 0) {
    return (
      <Stack space="medium">
        <JobSearchPanel totalCount={totalCount} isDraft={isDraft} />
        <Box display="flex" justifyContent="center" paddingTop="large">
          <EmptyState searchText={searchText} tab={tab} />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack space="medium">
      <JobSearchPanel totalCount={totalCount} isDraft={isDraft} />
      <Box>{children}</Box>
      <Box display="flex" justifyContent="center" paddingTop="xlarge">
        {totalCount > 0 && <PaginationSection totalCount={totalCount} />}
      </Box>
    </Stack>
  );
};
